<script>
    import CheckBoxAreaForInvoicesComponent from "./CheckBoxAreaForInvoicesComponent.svelte";
    import { onMount } from 'svelte';
    import CheckBoxAreForClassesComponent from "./CheckBoxAreForClassesComponent.svelte";
    import CompletedPaymentsList from './CompletedPaymentsListComponent.svelte';
    import NavBarReusable from "./NavbarReusable.svelte";
    import { getApiBase } from './apiBase.js';
    import { buildInvoiceClassWindow, retainVisibleSelections } from './invoiceClassWindow.js';

    export let studentId = null;
    export let embedded = false;
    let lastEmbeddedStudentId = null;
    const apiBase = getApiBase();

    function getHashQueryParam(name) {
        const hash = window.location.hash || '';
        const qIndex = hash.indexOf('?');
        if (qIndex === -1) return null;
        const params = new URLSearchParams(hash.slice(qIndex + 1));
        return params.get(name);
    }
    
    // ========== ADMIN USER SELECTION SECTION ==========
    let allStudents = [];
    let selectedStudentId = null;
    let selectedStudent = null;
    let isLoadingStudents = true;
    let studentsError = null;
    let studentQuery = '';
    let filteredStudents = [];
    let showStudentDropdown = false;
    
    // ========== NEGATIVE BALANCE USERS SECTION ==========
    let negativeBalanceUsers = [];
    let isLoadingNegativeBalances = true;
    let negativeBalancesError = null;
    let isRecalculatingBalances = false;
    let isRecalculatingSelectedStudent = false;
    let isSyncingSelectedStudent = false;
    // When true, only show debtors with NO upcoming classes (stale debt).
    let showStaleOnly = false;

    $: displayedNegativeBalanceUsers = showStaleOnly
        ? negativeBalanceUsers.filter(u => u.has_future_classes === false)
        : negativeBalanceUsers;
    
    // ========== INVOICE PAGE VARIABLES (IDENTICAL TO StandaloneInvoicesPage.svelte) ==========
    let selectedMonths = [];

    let invoiceSum = 0; // Only set after invoice is generated
    let paymentLink = '';
    let isFetchingPaymentLink = false;

    let selectedClasses = [];

    let invoiceData = null;
    let visibleOlderMonthCount = 0;
    let visibleFutureMonthCount = 0;
    $: classWindow = buildInvoiceClassWindow(invoiceData?.classes || [], {
        visibleOlderMonthCount,
        visibleFutureMonthCount,
    });
    let errorForInvoiceData = null;
    let isLoadingInvoiceData = false; // Don't load automatically until user is selected
    let backLinkUrl = "/admin"; // Back to admin page

    // If opened via Students dashboard deep link, make Back return there
    $: if (!embedded && getHashQueryParam('studentId')) {
        backLinkUrl = "/admin/students";
    }

    function showOneMorePastMonth() {
        visibleOlderMonthCount += 1;
    }

    function showOneLessPastMonth() {
        const nextMonthCount = Math.max(0, visibleOlderMonthCount - 1);
        const smallerWindow = buildInvoiceClassWindow(invoiceData?.classes || [], {
            visibleOlderMonthCount: nextMonthCount,
            visibleFutureMonthCount,
        });
        selectedClasses = retainVisibleSelections(selectedClasses, smallerWindow.visibleClasses);
        visibleOlderMonthCount = nextMonthCount;
    }

    function showOneMoreFutureMonth() {
        visibleFutureMonthCount += 1;
    }

    function showOneLessFutureMonth() {
        const nextMonthCount = Math.max(0, visibleFutureMonthCount - 1);
        const smallerWindow = buildInvoiceClassWindow(invoiceData?.classes || [], {
            visibleOlderMonthCount,
            visibleFutureMonthCount: nextMonthCount,
        });
        selectedClasses = retainVisibleSelections(selectedClasses, smallerWindow.visibleClasses);
        visibleFutureMonthCount = nextMonthCount;
    }

    // ========== ADMIN USER SELECTION FUNCTIONS ==========
    async function fetchAllStudents() {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            console.error('No JWT token found. User might not be logged in.');
            studentsError = 'Nepavyko rasti autentifikacijos duomenų.';
            isLoadingStudents = false;
            return;
        }

        try {
            const response = await fetch(`${apiBase}/studentplayground/admin/get-all-students/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const result = await response.json();
                allStudents = result.students || [];
                filteredStudents = allStudents;
                console.log('👥 ADMIN USER VIEW - Loaded students:', allStudents.length);
                studentsError = null;
            } else {
                const error = await response.json();
                console.log('Error fetching students:', error);
                studentsError = error.message || 'Nepavyko gauti studentų sąrašo.';
            }
        } catch (error) {
            console.log('Fetch error:', error);
            studentsError = 'Tinklo klaida. Patikrinkite interneto ryšį.';
        } finally {
            isLoadingStudents = false;
        }
    }

    async function fetchNegativeBalanceUsers() {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            console.error('No JWT token found. User might not be logged in.');
            negativeBalancesError = 'Nepavyko rasti autentifikacijos duomenų.';
            isLoadingNegativeBalances = false;
            return;
        }

        try {
            const response = await fetch(`${apiBase}/studentplayground/admin/get-negative-balance-users/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const result = await response.json();
                negativeBalanceUsers = result.users || [];
                console.log('💰 ADMIN USER VIEW - Loaded negative balance users:', negativeBalanceUsers.length);
                negativeBalancesError = null;
            } else {
                const error = await response.json();
                console.log('Error fetching negative balance users:', error);
                negativeBalancesError = error.message || 'Nepavyko gauti neigiamo balanso naudotojų.';
            }
        } catch (error) {
            console.log('Fetch error:', error);
            negativeBalancesError = 'Tinklo klaida. Patikrinkite interneto ryšį.';
        } finally {
            isLoadingNegativeBalances = false;
        }
    }

    async function recalculateSelectedStudentBalance(studentId) {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            console.error('No JWT token found. User might not be logged in.');
            alert('Nepavyko rasti autentifikacijos duomenų.');
            return;
        }

        isRecalculatingSelectedStudent = true;
        console.log(`🔄 Recalculating balance for student ${studentId}...`);

        try {
            const response = await fetch(`${apiBase}/studentplayground/admin/recalculate-student-balance/${studentId}/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await response.json();

            if (response.ok && result.status === 'success') {
                console.log('✅ Student balance recalculation completed:', result);

                // Refresh the negative balance users list (uses stored StudentBalance.current_balance)
                isLoadingNegativeBalances = true;
                await fetchNegativeBalanceUsers();
            } else {
                console.error('Error recalculating student balance:', result);
                alert(`Error recalculating student balance: ${result.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Fetch error:', error);
            alert('Network error. Please check your connection and try again.');
        } finally {
            isRecalculatingSelectedStudent = false;
        }
    }

    async function syncSelectedStudentChargesAndBalance(studentId) {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            alert('Nepavyko rasti autentifikacijos duomenų.');
            return;
        }

        isSyncingSelectedStudent = true;
        try {
            const response = await fetch(`${apiBase}/studentplayground/admin/sync-student-charges-and-balance/${studentId}/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await response.json();
            if (response.ok && result.status === 'success') {
                // Refresh the negative balance list and re-fetch invoice data for this student
                isLoadingNegativeBalances = true;
                await fetchNegativeBalanceUsers();
                fetchInvoiceData();
            } else {
                alert(`Sync failed: ${result.message || 'Unknown error'}`);
            }
        } catch (e) {
            console.error('Sync error:', e);
            alert('Network error. Please check your connection and try again.');
        } finally {
            isSyncingSelectedStudent = false;
        }
    }

    async function recalculateAllBalances() {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            console.error('No JWT token found. User might not be logged in.');
            alert('Nepavyko rasti autentifikacijos duomenų.');
            return;
        }

        isRecalculatingBalances = true;
        console.log('🔄 Starting balance recalculation...');

        try {
            const response = await fetch(`${apiBase}/studentplayground/admin/recalculate-all-balances/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await response.json();

            if (response.ok && result.status === 'success') {
                console.log('✅ Balance recalculation completed:', result);
                alert(`Balance recalculation completed!\n\nNegative balance users: ${result.negative_balance_count}`);
                
                // Refresh the negative balance users list
                isLoadingNegativeBalances = true;
                await fetchNegativeBalanceUsers();
            } else {
                console.error('Error recalculating balances:', result);
                alert(`Error recalculating balances: ${result.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Fetch error:', error);
            alert('Network error. Please check your connection and try again.');
        } finally {
            isRecalculatingBalances = false;
        }
    }

    async function selectStudent(student) {
        selectedStudent = student;
        selectedStudentId = student.id;
        studentQuery = `${student.first_name} ${student.last_name}`;
        showStudentDropdown = false;
        
        // Reset invoice data when changing student
        invoiceData = null;
        errorForInvoiceData = null;
        selectedClasses = [];
        
        // Recalculate this student's stored balance for admin lists (efficient single-user update)
        await recalculateSelectedStudentBalance(selectedStudentId);

        // Fetch invoice data for selected student
        fetchInvoiceData();
    }

    function filterStudents() {
        if (!studentQuery.trim()) {
            filteredStudents = allStudents;
            showStudentDropdown = false;
        } else {
            const query = studentQuery.toLowerCase();
            filteredStudents = allStudents.filter(student => 
                `${student.first_name} ${student.last_name}`.toLowerCase().includes(query) ||
                student.email.toLowerCase().includes(query)
            );
            showStudentDropdown = !selectedStudent && filteredStudents.length > 0;
        }
    }

    function clearStudentSelection() {
        selectedStudent = null;
        selectedStudentId = null;
        studentQuery = '';
        showStudentDropdown = false;
        filteredStudents = allStudents;
        
        // Reset invoice data
        invoiceData = null;
        errorForInvoiceData = null;
        selectedClasses = [];
    }

    // ========== INVOICE FUNCTIONS (IDENTICAL TO StandaloneInvoicesPage.svelte) ==========
    // Helper: calculate invoice sum as sum of amount_due for selected classes
    function calculateInvoiceSum() {
        if (!invoiceData || !invoiceData.classes) {
            return 0;
        }
        const sum = selectedClasses.reduce((sum, classId) => {
            const cls = invoiceData.classes.find(c => c.id === classId);
            // Ensure amount_due is always treated as a float number
            const amount = cls ? parseFloat(cls.amount_due) || 0.0 : 0.0;
            return sum + amount;
        }, 0.0);
        return Math.round(sum * 100) / 100; // Round to 2 decimal places to avoid floating point errors
    }

    // Update invoiceSum whenever selectedClasses changes
    $: {
        invoiceSum = calculateInvoiceSum();
    }

    // Fetch payment link - DISABLED for admin viewing (admin replica only)
    async function fetchPaymentLinkAndRedirect() {
        alert("🚫 ADMIN VIEW: Payment functionality is disabled for safety. This shows exactly what the user would see, but payments cannot be processed from this admin view.");
        return;
    }

    async function fetchInvoiceData() {
        if (!selectedStudentId) {
            console.error('No student selected');
            return;
        }

        const token = localStorage.getItem('jwtToken');
        if (!token) {
            console.error('No JWT token found. User might not be logged in.');
            errorForInvoiceData = 'Nepavyko rasti autentifikacijos duomenų. Prašome prisijungti iš naujo.';
            isLoadingInvoiceData = false;
            return;
        }

        isLoadingInvoiceData = true;

        try {
            // Use admin debug endpoint to get data for selected student
            // NO month filtering - fetch ALL data just like the user endpoint does
        const url = `${apiBase}/studentplayground/admin/debug-invoice-data/${selectedStudentId}/`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const result = await response.json();
                invoiceData = result;
                const outstandingWindow = buildInvoiceClassWindow(result.classes || [], {
                    visibleOlderMonthCount: Number.MAX_SAFE_INTEGER,
                });
                visibleOlderMonthCount = outstandingWindow.initialOlderMonthCount;
                visibleFutureMonthCount = 0;
                selectedClasses = [];
                errorForInvoiceData = null;
            } else {
                const error = await response.json();
                console.error('Error fetching invoice data:', error);
                errorForInvoiceData = error.message || 'Nepavyko gauti sąskaitos duomenų.';
            }
        } catch (error) {
            console.error('Fetch error:', error);
            errorForInvoiceData = 'Tinklo klaida. Patikrinkite interneto ryšį.';
        } finally {
            isLoadingInvoiceData = false;
        }
    }

    onMount(async () => {
        if (embedded && studentId) {
            const idNum = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;
            if (!Number.isNaN(idNum)) {
                lastEmbeddedStudentId = idNum;
                selectedStudentId = idNum;
                selectedStudent = null;
                isLoadingStudents = false;
                isLoadingNegativeBalances = false;
                studentsError = null;
                negativeBalancesError = null;
                negativeBalanceUsers = [];
                // Do not fetch here; the reactive embedded block below performs the initial fetch.
                return;
            }
        }

        await Promise.all([fetchAllStudents(), fetchNegativeBalanceUsers()]);

        // Full-page deep link support: #/admin/user-invoice-view?studentId=123
        const fromQuery = getHashQueryParam('studentId');
        if (fromQuery) {
            const idNum = parseInt(fromQuery, 10);
            if (!Number.isNaN(idNum)) {
                const match = allStudents.find(s => s.id === idNum);
                if (match) {
                    await selectStudent(match);
                } else {
                    selectedStudentId = idNum;
                    selectedStudent = null;
                    invoiceData = null;
                    errorForInvoiceData = null;
                    fetchInvoiceData();
                }
            }
        }
    });

    $: if (embedded && studentId) {
        const idNum = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;
        if (!Number.isNaN(idNum) && idNum !== lastEmbeddedStudentId) {
            lastEmbeddedStudentId = idNum;
            selectedStudentId = idNum;
            selectedStudent = null;
            invoiceData = null;
            errorForInvoiceData = null;
            fetchInvoiceData();
        }
    }

    // Reactive statement to filter students when search query changes
    $: if (studentQuery !== undefined) {
        filterStudents();
    }
</script>

<style>
    /* ========== STYLES FROM StandaloneInvoicesPage.svelte ========== */
    button span {
         font-weight: bolder;
         font-size:larger;
     }
  
     button {
        margin-top:10px;
         box-sizing: border-box;
         display: flex;
         flex-direction: row;
         justify-content: center;
         align-items: center;
         padding: 12px 20px;
         gap: 10px;
     
         width: 100%;
         height: 60px;
     
         /* Purple background */
         background-color: #fab45a;
     
         /* Black border */
         border: 2px solid #000000;
     
         /* Box shadow */
         box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
     
         /* Rounded corners */
         border-radius: 8px;
     
         /* Inside auto layout */
         flex: none;
         order: 0;
         align-self: stretch;
         flex-grow: 0;
     
         /* Remove default button styling */
         outline: none;
         cursor: pointer;
   
         /* Remove the default button background on focus */
         background-image: none;
         transition: background-color 0.3s ease, color 0.3s ease;
     }
  
     /* Button hover effect */
     button:hover {
         background-color: #14695b;
         color:white;
     }
  
     /* Text inside the button */
     span {
        margin-top: 5px;
        margin-bottom: 5px;
        font-size: 1rem;
        font-weight: bold;
     
     }
   form {
       padding-bottom: 10px;
       padding-top:15px;
   }
   span {
       font-weight: bold;
   }
   
   label {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 5px;
          display: block;
          color:#14695b;
          text-align: center;
      }

      .balance-info {
          margin: 1rem 0 1.5rem 0;
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #14695b;
          text-align: center;
      }

      .balance-info > div {
          margin-bottom: 8px;
      }

      .balance-label {
          font-weight: 600;
          color: #14695b;
          display: inline;
      }

      .balance-amount {
          font-weight: 700;
          font-size: 1.1em;
          color: #333;
          margin-left: 8px;
      }

      .balance-date {
          color: #666;
          font-size: 0.9em;
          margin-left: 8px;
      }

      .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          margin: 2rem 0;
      }

      .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #14695b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
      }

      @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
      }

      .loading-text {
          color: #666;
          font-size: 1rem;
          margin: 0;
      }

      .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          margin: 2rem 0;
          background-color: #fff3f3;
          border-radius: 8px;
          border-left: 4px solid #dc3545;
      }

      .error-text {
          color: #dc3545;
          font-size: 1rem;
          margin-bottom: 1rem;
          text-align: center;
      }

      .error-container button {
          background-color: #dc3545;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
      }

      .error-container button:hover {
          background-color: #c82333;
      }

      .payments-section {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 2px solid #e9ecef;
      }

      .payments-header {
          color: #14695b;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
      }

      .payments-description {
          color: #666;
          font-size: 1rem;
          margin-bottom: 1.5rem;
          line-height: 1.4;
      }

      .no-payments-section {
          margin-top: 2rem;
          padding: 2rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          text-align: center;
      }

      .no-payments-text {
          font-size: 1rem;
          color: #333;
      }

      .class-window-toggle {
          width: auto;
          height: auto;
          min-height: 36px;
          margin: 0;
          padding: 0.45rem 0.9rem;
          align-self: center;
          border: 1px solid #14695b;
          background-color: #fff7ec;
          color: #14695b;
          font-size: 0.9rem;
          font-weight: 600;
      }

      .class-window-controls {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
          margin: 0.65rem 0;
      }

      /* ========== ADMIN-SPECIFIC STYLES ========== */
      .admin-banner {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 0.75rem 1rem;
          margin-bottom: 1.5rem;
          border-radius: 8px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      }

      .admin-banner-title {
          font-size: 1.1rem;
          font-weight: bold;
          margin: 0 0 0.25rem 0;
      }

      .admin-banner-subtitle {
          font-size: 0.9rem;
          margin: 0;
          opacity: 0.95;
      }

      .student-selector {
          margin-bottom: 2rem;
          padding: 1.5rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #667eea;
      }

      .student-selector-label {
          color: #667eea;
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 1rem;
          display: block;
      }

      .form-help {
          font-size: 0.9rem;
          color: #666;
          margin-top: 0.5rem;
      }

      .input-container {
          position: relative;
          margin-top: 15px;
      }

      .search-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 0.9em;
          background-color: white;
          box-sizing: border-box;
      }

      .search-input:focus {
          outline: none;
          border-color: #14695b;
          box-shadow: 0 0 0 2px rgba(20, 105, 91, 0.2);
      }

      .autocomplete-list {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-top: none;
          border-radius: 0 0 6px 6px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }

      .autocomplete-list-item {
          padding: 0.25rem 0.5rem;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
          transition: background-color 0.2s ease;
      }

      .autocomplete-list-item:hover {
          background-color: #f8f9fa;
      }

      .autocomplete-list-item:last-child {
          border-bottom: none;
      }

      .student-option {
          display: flex;
          flex-direction: column;
          gap: 0;
      }

      .student-name {
          font-weight: 500;
          color: #333;
          font-size: 0.8em;
          line-height: 1.1;
      }

      .student-email {
          color: #666;
          font-size: 0.7em;
          line-height: 1.1;
      }

      .student-id {
          color: #667eea;
          font-size: 0.65em;
          font-weight: 500;
          line-height: 1.1;
      }

      .selected-student-badge {
          display: inline-flex;
          align-items: center;
          background-color: #d1ecf1;
          color: #0c5460;
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          font-size: 0.9rem;
          margin-top: 0.5rem;
      }

      .selected-student-badge .remove-selection {
          background: none;
          border: none;
          color: #0c5460;
          margin-left: 0.5rem;
          cursor: pointer;
          font-weight: bold;
          font-size: 1.2rem;
          padding: 0;
          width: auto;
          height: auto;
      }

      .selected-student-badge .remove-selection:hover {
          color: #062c33;
          background: none;
      }

      .selected-student-info {
          margin-top: 1rem;
          padding: 1rem;
          background-color: white;
          border-radius: 4px;
          border: 1px solid #ddd;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 15px;
      }

      .selected-student-details {
          flex: 1;
      }

      .student-info-label {
          font-weight: 600;
          color: #667eea;
      }

      .btn-clear-selection {
          padding: 8px 16px;
          background-color: #6c757d;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.85em;
          white-space: nowrap;
          transition: background-color 0.2s;
      }

      .btn-clear-selection:hover {
          background-color: #545b62;
      }

      /* Payment button override for admin view */
      .payment-disabled {
          background-color: #6c757d !important;
          cursor: not-allowed !important;
          opacity: 0.7;
      }

      .payment-disabled:hover {
          background-color: #5a6268 !important;
      }

      /* ========== NEGATIVE BALANCE USERS STYLES ========== */
      .negative-balance-container {
          margin-bottom: 2rem;
          padding: 1.5rem;
          background-color: #fff5f5;
          border-radius: 8px;
          border-left: 4px solid #dc3545;
      }

      .negative-balance-header {
          color: #dc3545;
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 8px;
      }

      .header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
      }

      .btn-recalculate {
          padding: 0;
          background-color: #667eea;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 1.5em;
          font-weight: 600;
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 40px;
          width: 80px;
          margin-top: 0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }

      .btn-recalculate:hover:not(:disabled) {
          background-color: #5568d3;
      }

      .btn-recalculate:disabled {
          background-color: #9ca3af;
          cursor: not-allowed;
          opacity: 0.6;
      }

      .btn-recalculate.active {
          background-color: #dc3545;
      }

      .btn-recalculate.active:hover:not(:disabled) {
          background-color: #c82333;
      }

      .negative-balance-footer {
          margin-top: 1rem;
          padding-top: 0.75rem;
          border-top: 1px dashed #f5c6cb;
          display: flex;
          justify-content: flex-end;
      }

      .btn-sync-student {
          padding: 8px 14px;
          height: auto;
          width: auto;
          background-color: #667eea;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 600;
          line-height: 1;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.2s;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }

      .btn-sync-student:hover:not(:disabled) {
          background-color: #5568d3;
      }

      .btn-sync-student:disabled {
          background-color: #9ca3af;
          cursor: not-allowed;
          opacity: 0.7;
      }

      .btn-sync-icon {
          font-size: 1.1em;
      }

      .btn-recalculate .spinner {
          width: 10px;
          height: 10px;
          border: 2px solid #fff;
          border-top: 2px solid transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
      }

      .negative-balance-count {
          background-color: #dc3545;
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 0.9rem;
          font-weight: bold;
      }

      .negative-balance-subtitle {
          color: #666;
          font-size: 0.9rem;
          margin-bottom: 1rem;
      }

      .negative-balance-list {
          max-height: 250px;
          overflow-y: auto;
          background-color: white;
          border-radius: 6px;
          border: 1px solid #ddd;
      }

      .negative-balance-list::-webkit-scrollbar {
          width: 6px;
      }

      .negative-balance-list::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
      }

      .negative-balance-list::-webkit-scrollbar-thumb {
          background: #dc3545;
          border-radius: 3px;
      }

      .negative-balance-list::-webkit-scrollbar-thumb:hover {
          background: #c82333;
      }

      .negative-balance-item {
          padding: 0.25rem 0.5rem;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: background-color 0.2s;
          cursor: pointer;
      }

      .negative-balance-item:hover {
          background-color: #f8f9fa;
      }

      .negative-balance-item:last-child {
          border-bottom: none;
      }

      .user-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0;
      }

      .user-name {
          font-weight: 500;
          color: #333;
          font-size: 0.8em;
          line-height: 1.1;
      }

      .user-email {
          color: #666;
          font-size: 0.7em;
          line-height: 1.1;
      }

      .user-id {
          color: #999;
          font-size: 0.65em;
          line-height: 1.1;
      }

      .balance-value {
          font-weight: 700;
          font-size: 0.8em;
          padding: 2px 6px;
          border-radius: 4px;
          white-space: nowrap;
      }

      .balance-negative {
          color: #dc3545;
          background-color: #ffebee;
      }

      .no-negative-balances {
          text-align: center;
          padding: 2rem;
          color: #28a745;
          background-color: white;
          border-radius: 6px;
          border: 1px solid #d4edda;
      }

      .no-negative-balances-icon {
          font-size: 2rem;
          margin-bottom: 0.5rem;
      }

      .no-negative-balances-text {
          font-weight: 500;
          font-size: 1rem;
      }
</style>

{#if !embedded}
<NavBarReusable label="Admin: View User's Invoice Page" backLink={backLinkUrl} />

<div class="admin-banner">
    <div class="admin-banner-title">👁️ Admin User View Mode</div>
    <div class="admin-banner-subtitle">You're seeing exactly what the selected user sees on their invoice page</div>
</div>

<!-- Negative Balance Users Section -->
<div class="negative-balance-container">
    <div class="header-row">
        <div class="negative-balance-header">
            💰 Users with Negative Balance
            {#if !isLoadingNegativeBalances && !negativeBalancesError}
                <span class="negative-balance-count">{displayedNegativeBalanceUsers.length}</span>
            {/if}
        </div>
        <button 
            type="button"
            class="btn-recalculate" 
            on:click={() => selectedStudentId ? recalculateSelectedStudentBalance(selectedStudentId) : recalculateAllBalances()}
            disabled={isRecalculatingBalances || isRecalculatingSelectedStudent}
            title={selectedStudentId ? "Recalculate selected student's balance" : "Recalculate all student balances"}
        >
            {#if isRecalculatingBalances || isRecalculatingSelectedStudent}
                <div class="spinner"></div>
            {:else}
                🔄
            {/if}
        </button>
        <button
            type="button"
            class="btn-recalculate"
            class:active={showStaleOnly}
            aria-pressed={showStaleOnly}
            on:click={() => showStaleOnly = !showStaleOnly}
            title={showStaleOnly
                ? "Showing only stale debt (no upcoming classes). Click to show all debtors."
                : "Filter to debtors with no upcoming classes (stale debt)."}
        >
            🕒
        </button>
    </div>
    <p class="negative-balance-subtitle">Quick access to users who owe money</p>
    
    {#if isLoadingNegativeBalances}
        <div class="loading-container">
            <div class="loading-spinner"></div>
            <p class="loading-text">Loading negative balances...</p>
        </div>
    {:else if negativeBalancesError}
        <div class="error-container">
            <p class="error-text">Error: {negativeBalancesError}</p>
            <button on:click={() => { isLoadingNegativeBalances = true; negativeBalancesError = null; fetchNegativeBalanceUsers(); }}>
                Try Again
            </button>
        </div>
    {:else if displayedNegativeBalanceUsers.length === 0}
        <div class="no-negative-balances">
            <div class="no-negative-balances-icon">✅</div>
            <p class="no-negative-balances-text">
                {#if showStaleOnly}
                    No stale debt — every debtor still has upcoming classes.
                {:else}
                    All users have positive or zero balance!
                {/if}
            </p>
        </div>
    {:else}
        <div class="negative-balance-list">
            {#each displayedNegativeBalanceUsers as user}
                <div 
                    class="negative-balance-item"
                    on:click={() => selectStudent(user)}
                    on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectStudent(user)}
                    role="button"
                    tabindex="0"
                >
                    <div class="user-info">
                        <span class="user-name">{user.first_name} {user.last_name}</span>
                        <span class="user-email">{user.email}</span>
                        <span class="user-id">ID: {user.id}</span>
                    </div>
                    <div class="balance-value balance-negative">
                        {user.current_balance.toFixed(2)} EUR
                    </div>
                </div>
            {/each}
        </div>
    {/if}

    {#if selectedStudentId}
        <div class="negative-balance-footer">
            <button
                type="button"
                class="btn-sync-student"
                on:click={() => syncSelectedStudentChargesAndBalance(selectedStudentId)}
                disabled={isSyncingSelectedStudent}
                title="Re-bill any finished classes that were missed, then recompute this student's balance."
            >
                {#if isSyncingSelectedStudent}
                    <div class="spinner"></div>
                    <span>Sinchronizuojama…</span>
                {:else}
                    <span class="btn-sync-icon">⚡</span>
                    <span>Sync charges + recalc balance for selected student</span>
                {/if}
            </button>
        </div>
    {/if}
</div>

<!-- Student Selection (Admin Only) -->
<div class="student-selector">
    <label for="student-search" class="student-selector-label">Select User to View As:</label>
    
    {#if isLoadingStudents}
        <div class="loading-container">
            <div class="loading-spinner"></div>
            <p class="loading-text">Loading users...</p>
        </div>
    {:else if studentsError}
        <div class="error-container">
            <p class="error-text">Error: {studentsError}</p>
            <button on:click={() => { isLoadingStudents = true; studentsError = null; fetchAllStudents(); }}>
                Try Again
            </button>
        </div>
    {:else}
        <div class="input-container">
            <input 
                id="student-search"
                type="text" 
                placeholder="Search user by name or email..."
                bind:value={studentQuery}
                on:input={() => {
                    if (selectedStudent && studentQuery !== `${selectedStudent.first_name} ${selectedStudent.last_name}`) {
                        selectedStudent = null;
                        selectedStudentId = null;
                        showStudentDropdown = true;
                    } else if (!selectedStudent) {
                        showStudentDropdown = studentQuery.trim().length > 0;
                    }
                }}
                autocomplete="off"
                class="search-input"
            />
            
            {#if selectedStudent}
                <div class="selected-student-badge">
                    ✓ {selectedStudent.first_name} {selectedStudent.last_name} ({selectedStudent.email})
                    <button 
                        type="button" 
                        class="remove-selection"
                        on:click={() => {
                            selectedStudent = null;
                            selectedStudentId = null;
                            studentQuery = '';
                            showStudentDropdown = false;
                            filteredStudents = allStudents;
                            invoiceData = null;
                            errorForInvoiceData = null;
                            selectedClasses = [];
                        }}
                    >
                        ×
                    </button>
                </div>
            {/if}
            
            {#if showStudentDropdown}
                <div class="autocomplete-list">
                    {#each filteredStudents.slice(0, 8) as student}
                        <div 
                            class="autocomplete-list-item"
                            on:click={() => selectStudent(student)}
                            role="button"
                            tabindex="0"
                            on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectStudent(student)}
                        >
                            <div class="student-option">
                                <span class="student-name">{student.first_name} {student.last_name}</span>
                                <span class="student-email">{student.email}</span>
                                <span class="student-id">ID: {student.id}</span>
                            </div>
                        </div>
                    {/each}
                </div>
            {/if}
        </div>
        <div class="form-help">Start typing to search for users by name or email</div>
        
        {#if selectedStudent}
            <div class="selected-student-info">
                <div class="selected-student-details">
                    <div><span class="student-info-label">Viewing as:</span> {selectedStudent.first_name} {selectedStudent.last_name}</div>
                    <div><span class="student-info-label">Email:</span> {selectedStudent.email}</div>
                    <div><span class="student-info-label">ID:</span> {selectedStudent.id}</div>
                </div>
                <button 
                    type="button" 
                    class="btn-clear-selection" 
                    on:click={clearStudentSelection}
                >
                    Clear Selection
                </button>
            </div>
        {/if}
    {/if}
</div>
{/if}

<!-- Invoice Page View (EXACT REPLICA of what user sees) -->
{#if selectedStudentId}
    {#if isLoadingInvoiceData}
        <div class="loading-container">
            <div class="loading-spinner"></div>
            <p class="loading-text">Kraunamos sąskaitos...</p>
        </div>
    {:else if errorForInvoiceData}
        <div class="error-container">
            <p class="error-text">Klaida: {errorForInvoiceData}</p>
            <button on:click={() => { isLoadingInvoiceData = true; errorForInvoiceData = null; fetchInvoiceData(); }}>
                Bandyti dar kartą
            </button>
        </div>
    {:else if invoiceData}
        <div class="balance-info">
            <div>
                <span class="balance-label">Dabartinis balansas:</span>
                <span class="balance-amount">{invoiceData.balance} EUR</span>
            </div>
            <div>
                <span class="balance-label">Paskutinį kartą atnaujinta:</span>
                <span class="balance-date">{new Date(invoiceData.last_updated_date).toLocaleDateString('lt-LT')}</span>
            </div>
        </div>

        <form on:submit|preventDefault={fetchPaymentLinkAndRedirect}>
            <label for="class-select">Pasirinkite Pamokas:</label>
            {#if classWindow.canShowMorePast || classWindow.canShowLessPast}
                <div class="class-window-controls" role="group" aria-label="Ankstesni mėnesiai">
                    {#if classWindow.canShowMorePast}
                        <button
                            type="button"
                            class="class-window-toggle"
                            on:click={showOneMorePastMonth}
                            aria-expanded={visibleOlderMonthCount > 0}
                            aria-controls="admin-invoice-class-list"
                        >
                            Rodyti daugiau
                        </button>
                    {/if}
                    {#if classWindow.canShowLessPast}
                        <button
                            type="button"
                            class="class-window-toggle"
                            on:click={showOneLessPastMonth}
                            aria-expanded="true"
                            aria-controls="admin-invoice-class-list"
                        >
                            Rodyti mažiau
                        </button>
                    {/if}
                </div>
            {/if}

            <div id="admin-invoice-class-list">
                <CheckBoxAreForClassesComponent
                    classes={classWindow.visibleClasses}
                    bind:selectedClasses
                />
            </div>

            {#if classWindow.canShowMoreFuture || classWindow.canShowLessFuture}
                <div class="class-window-controls" role="group" aria-label="Vėlesni mėnesiai">
                    {#if classWindow.canShowMoreFuture}
                        <button
                            type="button"
                            class="class-window-toggle"
                            on:click={showOneMoreFutureMonth}
                            aria-expanded={visibleFutureMonthCount > 0}
                            aria-controls="admin-invoice-class-list"
                        >
                            Rodyti daugiau
                        </button>
                    {/if}
                    {#if classWindow.canShowLessFuture}
                        <button
                            type="button"
                            class="class-window-toggle"
                            on:click={showOneLessFutureMonth}
                            aria-expanded="true"
                            aria-controls="admin-invoice-class-list"
                        >
                            Rodyti mažiau
                        </button>
                    {/if}
                </div>
            {/if}

            <p>Viso: {Math.round(invoiceSum)} EUR</p>

            <button type="submit" disabled={isFetchingPaymentLink} class="payment-disabled">
                <span>{isFetchingPaymentLink ? 'Kuriama nuoroda...' : 'Apmokėti'}</span>
            </button>
        </form>

        {#if invoiceData.previous_payments.length > 0}
            <div class="payments-section">
                <h2 class="payments-header">Atlikti mokėjimai</h2>
                <p class="payments-description">Čia galite peržiūrėti savo mokėjimo istoriją ir atsisiųsti sąskaitas.</p>
                <CompletedPaymentsList payments={invoiceData.previous_payments} />
            </div>
        {:else}
            <div class="no-payments-section">
                <h2 class="payments-header">Atlikti mokėjimai</h2>
                <p class="no-payments-text">Kol kas neturite atliktų mokėjimų.</p>
            </div>
        {/if}
    {/if}
{:else}
    <div class="loading-container">
        <p class="loading-text">👆 Please select a user above to view their invoice page</p>
    </div>
{/if}

